import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  CardActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Star as FeaturedIcon,
  CalendarToday as DateIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import axios from 'axios';
import StoryDialog from './StoryDialog';
import BulkUploadDialog from './BulkUploadDialog';

const StoriesManagement = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await axios.get('/api/stories/admin/all');
      setStories(response.data.stories);
    } catch (error) {
      console.error('Error fetching stories:', error);
      setError('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStory = async (storyId, storyTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${storyTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`/api/stories/admin/${storyId}`);
      fetchStories();
    } catch (error) {
      console.error('Error deleting story:', error);
      setError('Failed to delete story');
    }
  };

  const handleEditStory = (story) => {
    setEditingStory(story);
    setDialogOpen(true);
  };

  const handleAddStory = () => {
    setEditingStory(null);
    setDialogOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (loading) {
    return (
      <Box textAlign="center" py={4}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading stories...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Stories Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddStory}
          >
            Add New Story
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setBulkUploadOpen(true)}
          >
            Bulk Upload (.docx)
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {stories.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No stories yet
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Create your first story to get started with your blog.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddStory}
            >
              Create First Story
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {stories.map((story) => (
            <Grid item xs={12} md={6} lg={4} key={story.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {story.image_url ? (
                  <CardMedia
                    component="img"
                    height="140"
                    image={`${import.meta.env.VITE_API_URL}${story.image_url}`}
                    alt={story.image_alt || story.title}
                    sx={{ objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 140,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'grey.100'
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                  </Box>
                )}
                
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={1}>
                    <Chip
                      label={story.is_published ? 'Published' : 'Draft'}
                      color={story.is_published ? 'success' : 'default'}
                      size="small"
                    />
                    {story.is_featured && (
                      <Chip
                        icon={<FeaturedIcon />}
                        label="Featured"
                        color="primary"
                        size="small"
                      />
                    )}
                  </Box>
                  
                  <Typography variant="h6" component="h3" gutterBottom>
                    {story.title}
                  </Typography>
                  
                  {story.excerpt ? (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {story.excerpt}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {stripHtml(story.content).substring(0, 100)}...
                    </Typography>
                  )}
                  
                  <Box display="flex" alignItems="center" gap={2} mt={2}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <DateIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(story.published_date)}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <ViewIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {story.view_count}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
                
                <CardActions>
                  <IconButton 
                    size="small" 
                    onClick={() => handleEditStory(story)}
                    title="Edit story"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDeleteStory(story.id, story.title)}
                    title="Delete story"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                  {story.is_published && (
                    <Button
                      size="small"
                      component="a"
                      href={`/stories/${story.slug}`}
                      target="_blank"
                    >
                      View
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <StoryDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingStory(null);
        }}
        story={editingStory}
        onSave={fetchStories}
      />

      <BulkUploadDialog
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onSuccess={fetchStories}
      />
    </Box>
  );
};

export default StoriesManagement;